<!-- ModalProyectosPedido.vue — Vista de proyectos PDF de un pedido (solo lectura, para Tere) -->
<template>
  <div class="modal fade" id="modalProyectosPedido" tabindex="-1" ref="el">
    <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
      <div class="modal-content divisan-modal border-0">
        <!-- Header -->
        <div class="modal-header border-0 pb-0">
          <div class="modal-titulo-bloque">
            <div class="modal-titulo-linea">
              <i class="bi bi-file-earmark-pdf text-danger icono-titulo"></i>
              <span class="fw-bold text-cafe titulo-texto">
                Proyectos — Pedido #{{ pedido?.numero_pedido }}
              </span>
            </div>
            <div class="cliente-info-bloque" v-if="pedido">
              <span class="badge-cliente-num">{{ pedido.cliente_numero }}</span>
              <span class="cliente-nombre-header">{{ pedido.cliente }}</span>
            </div>
          </div>
          <button
            type="button"
            class="btn-close flex-shrink-0 ms-2"
            data-bs-dismiss="modal"
          ></button>
        </div>

        <div class="modal-body pt-3">
          <!-- Sin proyectos -->
          <div v-if="!proyectos.length" class="vacio-container">
            <i class="bi bi-file-earmark-pdf"></i>
            <p class="mt-2 mb-0">Sin proyectos adjuntos</p>
            <small>Este pedido no tiene PDFs de diseño registrados</small>
          </div>

          <!-- Lista de proyectos -->
          <div v-else class="proyectos-lista">
            <small class="text-cafe fw-bold d-block mb-3">
              <i class="bi bi-cloud-check me-1 text-verde"></i>
              {{ proyectos.length }} proyecto(s) disponible(s)
            </small>

            <div v-for="(p, idx) in proyectos" :key="p.id" class="proyecto-card">
              <!-- Header del card -->
              <div class="proyecto-header">
                <!-- Nombre archivo + contador -->
                <div class="proyecto-meta">
                  <i class="bi bi-file-earmark-pdf-fill text-danger icono-pdf"></i>
                  <span class="proyecto-nombre">{{ nombreArchivo(p.archivo) }}</span>
                  <span class="proyecto-idx text-muted ms-1">
                    {{ idx + 1 }}/{{ proyectos.length }}
                  </span>
                </div>
                <!-- Botones -->
                <div class="proyecto-acciones">
                  <a
                    :href="p.archivo"
                    target="_blank"
                    class="btn-descargar"
                    title="Abrir en nueva pestaña"
                  >
                    <i class="bi bi-box-arrow-up-right"></i>
                    <span class="btn-label">Abrir</span>
                  </a>
                  <button
                    class="btn-toggle-preview"
                    @click="p._preview = !p._preview"
                    :title="p._preview ? 'Ocultar preview' : 'Ver PDF'"
                  >
                    <i class="bi" :class="p._preview ? 'bi-eye-slash' : 'bi-eye'"></i>
                    <span class="btn-label">{{ p._preview ? "Ocultar" : "Ver" }}</span>
                  </button>
                </div>
              </div>

              <!-- Preview embebido -->
              <div v-if="p._preview" class="preview-container">
                <embed :src="p.archivo" type="application/pdf" class="pdf-embed" />
              </div>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="modal-footer border-0 pt-0">
          <button class="btn btn-cerrar" data-bs-dismiss="modal">Cerrar</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { Modal } from "bootstrap";

const el = ref(null);
const pedido = ref(null);
const proyectos = ref([]);
let modalInstance = null;

onMounted(() => {
  modalInstance = new Modal(el.value);
});

function abrir(p) {
  pedido.value = p;
  proyectos.value = (p.proyectos || []).map((pr) => ({ ...pr, _preview: false }));
  modalInstance.show();
}
defineExpose({ abrir });

function nombreArchivo(url) {
  if (!url) return "archivo.pdf";
  return decodeURIComponent(url.split("/").pop().split("?")[0]);
}
</script>

<style scoped>
.text-cafe {
  color: var(--d-brown);
}
.text-verde {
  color: var(--d-green);
}

/* ── Modal base ─────────────────────────────────────────── */
.divisan-modal {
  background-color: var(--d-cream);
  border: 2px solid var(--d-brown);
  border-radius: 20px;
}

/* ── Header ─────────────────────────────────────────────── */
.modal-header {
  align-items: flex-start;
}

.modal-titulo-bloque {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0; /* permite que el flex-child se encoja */
  flex: 1;
}

.modal-titulo-linea {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap; /* en móvil muy pequeño puede bajar */
}

.icono-titulo {
  font-size: 1rem;
  flex-shrink: 0;
}

.titulo-texto {
  font-size: 0.92rem; /* bajado de h5 (1rem+) */
  line-height: 1.3;
  word-break: break-word;
}

/* Línea cliente: badge + nombre */
.cliente-info-bloque {
  display: flex;
  align-items: baseline;
  gap: 6px;
  flex-wrap: wrap; /* si el nombre es largo, baja a segunda línea */
  min-width: 0;
}

.badge-cliente-num {
  background: var(--d-green-dark);
  color: var(--d-cream);
  font-family: var(--d-font-mono);
  font-size: 0.72rem; /* ligeramente más pequeño */
  font-weight: 700;
  border-radius: 5px;
  padding: 1px 6px;
  flex-shrink: 0;
}

.cliente-nombre-header {
  color: var(--d-green);
  font-size: 0.78rem; /* más pequeño que antes */
  font-weight: 700;
  line-height: 1.3;
  word-break: break-word;
  overflow-wrap: break-word;
}

/* ── Vacío ──────────────────────────────────────────────── */
.vacio-container {
  text-align: center;
  padding: 2.5rem;
  color: var(--d-brown);
  opacity: 0.4;
  font-weight: 700;
  font-size: 0.85rem;
}
.vacio-container i {
  font-size: 2.5rem;
  display: block;
}
.vacio-container small {
  font-weight: 400;
  font-size: 0.78rem;
}

/* ── Lista proyectos ────────────────────────────────────── */
.proyectos-lista {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.proyecto-card {
  border: 1px solid rgba(96, 108, 56, 0.3);
  border-radius: 12px;
  background: white;
  overflow: hidden;
}

/* Header del card: nombre arriba, botones abajo en móvil */
.proyecto-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.6rem 0.85rem;
  background: rgba(96, 108, 56, 0.06);
  border-bottom: 1px solid rgba(96, 108, 56, 0.12);
  gap: 8px;
  flex-wrap: wrap; /* en pantallas angostas: botones bajan */
}

.proyecto-meta {
  display: flex;
  align-items: center;
  min-width: 0;
  flex: 1;
  gap: 4px;
}

.icono-pdf {
  font-size: 0.9rem;
  flex-shrink: 0;
}

.proyecto-nombre {
  font-weight: 700;
  font-size: 0.8rem; /* reducido de 0.88rem */
  color: var(--d-green-dark);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  flex: 1;
}

.proyecto-idx {
  font-size: 0.7rem;
  flex-shrink: 0;
  white-space: nowrap;
}

/* Botones: siempre juntos, no se rompen entre sí */
.proyecto-acciones {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

/* ── Botones ────────────────────────────────────────────── */
.btn-toggle-preview,
.btn-descargar {
  font-size: 0.74rem;
  font-weight: 700;
  border-radius: 6px;
  padding: 4px 10px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: 0.15s;
  white-space: nowrap;
  flex-shrink: 0;
  cursor: pointer;
}

.btn-toggle-preview {
  background: none;
  border: 1px solid var(--d-green);
  color: var(--d-green);
}
.btn-toggle-preview:hover {
  background: var(--d-green);
  color: var(--d-cream);
}

.btn-descargar {
  background: rgba(64, 34, 8, 0.06);
  border: 1px solid rgba(64, 34, 8, 0.25);
  color: var(--d-brown);
  text-decoration: none;
}
.btn-descargar:hover {
  background: var(--d-brown);
  color: var(--d-cream);
}

/* ── Preview ────────────────────────────────────────────── */
.preview-container {
  border-top: 1px solid rgba(96, 108, 56, 0.12);
  background: #f0f0f0;
}
.pdf-embed {
  width: 100%;
  height: 520px;
  display: block;
  border: none;
}

/* ── Footer ─────────────────────────────────────────────── */
.btn-cerrar {
  background: none;
  border: 1.5px solid var(--d-green);
  color: var(--d-green);
  font-weight: 700;
  font-size: 0.85rem;
  border-radius: 8px;
  padding: 6px 20px;
}
.btn-cerrar:hover {
  background: rgba(96, 108, 56, 0.08);
}

/* ── Responsive móvil ───────────────────────────────────── */
@media (max-width: 600px) {
  .titulo-texto {
    font-size: 0.84rem;
  }

  /* En móvil los botones se comprimen: solo icono */
  .btn-label {
    display: none;
  }

  .btn-toggle-preview,
  .btn-descargar {
    padding: 5px 8px;
    font-size: 0.8rem;
  }

  .proyecto-nombre {
    font-size: 0.74rem;
  }

  .pdf-embed {
    height: 360px;
  }

  .proyecto-header {
    flex-wrap: nowrap; /* nombre + botones en una sola fila */
  }
}

@media (max-width: 400px) {
  .cliente-nombre-header {
    font-size: 0.72rem;
  }
  .titulo-texto {
    font-size: 0.78rem;
  }
}
</style>
